import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import {
  SearchMagnifier,
} from 'AppSrc/core/components/common/svg-icons';
import { setErrorAlert } from 'AppSrc/core/actions/actions-error-alert';
import { connect } from 'react-redux';

import { InlineErrors } from './sub-components/inline-errors';
import { FieldTitle } from './sub-components/field-title';
import {
  getFieldPropShape,
  formPropShape,
} from './data-structure/formik-proptypes';
import {
  LookupTableResults,
  lookupTableColumnShape,
} from './sub-components/lookup-table-components/lookup-table-results';
import 'AppSrc/core/style/components/form/form-base-components/v-f-lookup-table.css';

const MIN_SEARCH_CHARACTERS = 2;

/*
  VFLookupTable is a component that consists of a search box and a search results table.
  The user selects a value by selecting one of the results from the table.
  This was built specifically for relationships lookups, and as such is a little complicated because the shape of the search result
  and the columns shown, needs to be able to be different from the resulting value.
*/
class VFLookupTableUnconnected extends PureComponent {
  constructor(props) {
    super(props);
    this.updateValue = this.updateValue.bind(this);
    this.setTouched = this.setTouched.bind(this);
    this.updateSearch = this.updateSearch.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.performSearch = this.performSearch.bind(this);
    const initialValue = props.field.value;
    const { getInitialSearchQueryForValue } = props.vFParams;
    const searchQuery = initialValue && getInitialSearchQueryForValue ? getInitialSearchQueryForValue(initialValue) : '';
    this.state = {
      // the query sent for the search
      searchQuery,
      // the value typed into the search box
      // this only becomes the search query when the search has been performed
      searchBoxValue: searchQuery,
      searchResults: [],
      searchPerformed: false,
    };
  }

  componentDidMount() {
    // perform search on mount
    // for when an initial search query was provided
    this.performSearch();
  }

  setTouched() {
    const {
      field,
    } = this.props;

    const {
      name,
    } = field;

    this.props.form.setFieldTouched(name, true);
  }

  updateValue(value) {
    const {
      field,
    } = this.props;

    const {
      name,
    } = field;

    this.props.form.setFieldValue(name, value);
  }

  updateSearch(e) {
    const searchBoxValue = e.target.value;
    this.setState({ searchBoxValue });
  }

  isQueryValid(searchQuery) {
    return searchQuery && !(searchQuery.length < MIN_SEARCH_CHARACTERS);
  }

  performSearch() {
    const { searchBoxValue } = this.state;
    const { performSearch } = this.props.vFParams;
    const validQuery = this.isQueryValid(searchBoxValue);
    if (performSearch && validQuery) {
      performSearch(searchBoxValue)
        .then((results) => {
          this.setState({
            searchResults: results,
            searchPerformed: true,
            searchQuery: searchBoxValue,
          });
        })
        .catch((error) => {
          console.error(error);
          this.props.searchError();
        });
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.performSearch();
    }
  }

  render() {

    const {
      vFParams,
      field,
      form,
    } = this.props;

    const {
      name,
      value,
    } = field;

    const formErrors = form.errors;
    const touched = form.touched[name];


    const errors = formErrors[name];

    const inlineErrors = errors && errors.inlineErrors ? errors.inlineErrors : null;

    const {
      fieldTitle,
      placeholder,
      resultsColumns,
      getSelectedIdForValue,
    } = vFParams;

    const {
      searchQuery,
      searchBoxValue,
      searchResults,
      searchPerformed,
    } = this.state;

    const validQuery = this.isQueryValid(searchBoxValue);
    const buttonDisabled = validQuery ? false : 'disabled';
    // value for the selected row id in the results table
    // use the getSelectedIdForValue function from vfParams to parse the value into an id for the table
    const selectedId = value && getSelectedIdForValue ? getSelectedIdForValue(value) : null;
    const className = `v-f-field-type v-f-lookup-table v-f-lookup-table-${name}`;
    return (
      <div
        className={className}
      >
        <div className="v-f-lookup-table-element-wrapper">
          {
            fieldTitle && (
              <FieldTitle
                fieldTitle={fieldTitle}
              />
            )
          }
          <div className="v-f-lookup-table-search-wrapper">
            <input
              className="v-f-lookup-table-search"
              value={searchBoxValue}
              onChange={this.updateSearch}
              placeholder={placeholder}
              onKeyDown={this.handleKeyDown}
            />
            <div className="v-f-lookup-table-search-svg">
              <button
                type="button"
                disabled={buttonDisabled}
                onClick={this.performSearch}
              >
                <SearchMagnifier />
              </button>
            </div>
          </div>
          {
            inlineErrors && touched && (
              <InlineErrors
                inlineErrors={inlineErrors}
              />
            )
          }
        </div>
        { searchPerformed && (
          <LookupTableResults
            searchQuery={searchQuery}
            searchResults={searchResults}
            resultsColumns={resultsColumns}
            updateValue={this.updateValue}
            selectedId={selectedId}
          />
        )}
      </div>
    );
  }
}


// This is a reference to the component. This is needed as the value for each form field is displayed on a change summary
// page but this is dependant on what type of form field is used
const VF_LOOKUP_TABLE = 'v-f-lookup-table';


VFLookupTableUnconnected.propTypes = {
  vFParams: PropTypes.shape({
    fieldTitle: PropTypes.string,
    placeholder: PropTypes.string,
    // the columns that should be displayed in the results table
    // array of id-label pairs
    resultsColumns: PropTypes.arrayOf(lookupTableColumnShape),
    // function to parse a value for the selected row id in the table
    getSelectedIdForValue: PropTypes.func,
    // function to parse an existing value for an initial search query
    getInitialSearchQueryForValue: PropTypes.func,
    // function to perform the search and return a promise with the formatted search results
    performSearch: PropTypes.func,
  }),
  field: getFieldPropShape(PropTypes.obj),
  form: formPropShape,
  // DVB the following standard props don't yet apply to this field type
  // but may be needed in the future
  // optional: PropTypes.bool,
  // readOnly: PropTypes.bool,
  searchError: PropTypes.func,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  searchError() {
    dispatch(setErrorAlert({
      errorTitle: 'Unable to perform search',
      errorText: 'Please try again later',
    }));
  },
});

const VFLookupTable = connect(
  null,
  mapDispatchToProps,
)(VFLookupTableUnconnected);


// value is typically an object, although also adding support for a string value
const noValueVFLookupTable = (value) => {
  if (value === null) {
    return true;
  }
  // if the value is of type string, check it's length
  if (typeof value === 'string') {
    return value.trim().length < 1;
  }
  // otherwise the value is an object. non null is sufficient
  return false;
};

export {
  VFLookupTable,
  noValueVFLookupTable,
  VF_LOOKUP_TABLE,
};
